import React, { useState } from 'react';
import {
    ChevronDown,
    Building2,
    ExternalLink,
    Linkedin,
    Globe,
    Twitter,
    Facebook
} from 'lucide-react';
import { LogoPlaceholder } from './CommonComponents';
import { fallbackLogos } from '../../assets/logos/companyLogos';
import { usePortfolioTranslation } from '../../hooks/usePortfolioTranslation';

export const TimelineItem = ({ data, isOpen, onClick, isLast }) => {
    const { translateExperience } = usePortfolioTranslation();
    const [imageError, setImageError] = useState(false);

    const handleImageError = () => {
        setImageError(true);
    };

    return (
        <div className="relative pl-8 sm:pl-12 pb-12 last:pb-0">
            {/* Connecting Line */}
            {!isLast && (
                <div className="absolute left-[10px] sm:left-[11px] top-[30px] bottom-0 w-0.5 bg-[#732424]/30" />
            )}

            {/* Timeline Dot */}
            <div
                className={`absolute left-[2px] sm:left-[3px] top-[24px] w-4 h-4 sm:w-5 sm:h-5 rounded-full border-4 transition-all duration-500 z-10 ${isOpen
                    ? 'bg-[#0d0d0d] border-[#d92323] scale-110 shadow-[0_0_10px_#d92323]'
                    : 'bg-[#0d0d0d] border-[#732424] hover:border-[#d92323]'
                    }`}
            />

            {/* Interactive Card */}
            <div
                onClick={onClick}
                className={`
          cursor-pointer relative bg-[#141414] border rounded-xl p-6 transition-all duration-300 group
          ${isOpen
                        ? 'border-[#d92323] shadow-[0_0_20px_rgba(217,35,35,0.1)] bg-[#1a1a1a]'
                        : 'border-[#732424]/20 hover:border-[#d92323]/50 hover:bg-[#1a1a1a]/50'}
        `}
            >
                <div className="flex flex-col sm:flex-row justify-between sm:items-start gap-4">
                    <div className="flex gap-4 flex-1">
                        {/* Company Logo */}
                        <div className="flex-shrink-0 mt-1">
                            {data.logo && !imageError ? (
                                <img
                                    src={data.logo}
                                    alt={`${data.company} logo`}
                                    className="w-12 h-12 rounded-lg object-contain transition-transform group-hover:scale-110 duration-300 company-logo"
                                    onError={handleImageError}
                                />
                            ) : fallbackLogos[data.company] ? (
                                <img
                                    src={fallbackLogos[data.company]}
                                    alt={`${data.company} logo`}
                                    className="w-12 h-12 rounded-lg object-contain transition-transform group-hover:scale-110 duration-300 company-logo"
                                />
                            ) : (
                                <LogoPlaceholder name={data.company} />
                            )}
                        </div>

                        <div className="flex-1">
                            <div className="flex items-start gap-2 mb-2">
                                <h3 className={`text-xl font-bold transition-colors duration-300 ${isOpen ? 'text-[#d92323]' : 'text-white'}`}>
                                    {translateExperience(data.translationKey)?.role || data.role}
                                </h3>
                                {data.companyUrl && (
                                    <a
                                        href={data.companyUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#7b7b7b] hover:text-[#d92323] transition-colors mt-1"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                    </a>
                                )}
                            </div>
                            <div className="flex items-center gap-2 text-[#7b7b7b] text-sm mb-2">
                                <Building2 className="w-4 h-4" />
                                <span className="font-medium">{data.company}</span>
                                <span className="w-1 h-1 rounded-full bg-[#732424]"></span>
                                <span>{data.location}</span>
                            </div>

                            {/* Social Links */}
                            {data.socialLinks && (
                                <div className="flex items-center gap-2">
                                    {data.socialLinks.linkedin && (
                                        <a
                                            href={data.socialLinks.linkedin}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#7b7b7b] hover:text-[#0077b5] transition-colors social-link"
                                            onClick={(e) => e.stopPropagation()}
                                            title="LinkedIn"
                                        >
                                            <Linkedin className="w-4 h-4" />
                                        </a>
                                    )}
                                    {data.socialLinks.website && (
                                        <a
                                            href={data.socialLinks.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#7b7b7b] hover:text-[#d92323] transition-colors social-link"
                                            onClick={(e) => e.stopPropagation()}
                                            title="Website"
                                        >
                                            <Globe className="w-4 h-4" />
                                        </a>
                                    )}
                                    {data.socialLinks.twitter && (
                                        <a
                                            href={data.socialLinks.twitter}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#7b7b7b] hover:text-[#1da1f2] transition-colors social-link"
                                            onClick={(e) => e.stopPropagation()}
                                            title="Twitter"
                                        >
                                            <Twitter className="w-4 h-4" />
                                        </a>
                                    )}
                                    {data.socialLinks.facebook && (
                                        <a
                                            href={data.socialLinks.facebook}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#7b7b7b] hover:text-[#1877f2] transition-colors social-link"
                                            onClick={(e) => e.stopPropagation()}
                                            title="Facebook"
                                        >
                                            <Facebook className="w-4 h-4" />
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2 sm:mt-0">
                        <span className="text-[#d92323] text-xs font-mono bg-[#d92323]/10 px-2 py-1 rounded border border-[#d92323]/20 whitespace-nowrap">
                            {data.period}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-[#7b7b7b] transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#d92323]' : ''}`} />
                    </div>
                </div>

                {/* Expanded Content with Smooth Animation */}
                <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] mt-4 pt-4 border-t border-[#732424]/20' : 'grid-rows-[0fr]'}`}>
                    <div className="overflow-hidden">
                        <ul className="space-y-2 text-[#7b7b7b] text-sm leading-relaxed list-disc list-inside marker:text-[#d92323]">
                            {(translateExperience(data.translationKey)?.description || data.description).map((item, i) => (
                                <li key={i} className="pl-2 -indent-2 ml-4">{item}</li>
                            ))}
                        </ul>
                        <div className="flex flex-wrap gap-2 mt-4">
                            {data.tags.map((tag, i) => (
                                <span key={i} className="text-xs text-[#ffffff]/70 bg-[#0d0d0d] border border-[#732424]/20 px-2 py-1 rounded">
                                    #{tag}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};